import { ChangeEvent } from 'react';
import { routePostUploadFile, routePostUploadImage } from 'services/ApiCustomerSupportFormService';
import { rm } from 'utils';
import { Action } from './ImageUpload';
import { UploadFileResponseModel } from 'models/UploadFileResponseModel';

type ChangeProps = {
  e: ChangeEvent<HTMLInputElement>;
  dispatch: (action: Action) => void;
  ref: React.RefObject<HTMLFormElement>;
  onChange: (id: string, imageUrl: string, token?: string) => void;
  id: string;
  useZendesk?: boolean;
};

interface ApiResponse {
  message: string[];
  success: boolean;
}

interface ZendeskApiResponse {
  success: boolean;
  data: UploadFileResponseModel;
  message: string[];
}

const MAX_FILE_SIZE_BYTES = 10485760; // 10MB

export const changeImage = ({ e, dispatch, ref, onChange, id, useZendesk }: ChangeProps) => {
  if (!ref.current) {
    return;
  }
  const formData = new FormData(ref.current);
  const fileInput = e.target;
  const files = fileInput.files;
  if (!files || files.length === 0) {
    e.target.value = '';
    return;
  }
  const multipleFiles = files.length > 1;
  const fileSizeTooLarge = files[0].size > MAX_FILE_SIZE_BYTES;

  if (multipleFiles) {
    e.preventDefault();
    e.target.value = '';
    dispatch({ type: 'error', value: rm('customerpage_returns_image_error_count') });
    return;
  }

  if (fileSizeTooLarge) {
    e.preventDefault();
    e.target.value = '';
    dispatch({ type: 'error', value: rm('customerpage_returns_image_error_size') });
    return;
  }

  dispatch({ type: 'loading' });
  dispatch({ type: 'error', value: '' });
  if (useZendesk) {
    uploadAndGetImageZendesk({ formData, fileInput, dispatch, onChange, id });
  } else {
    uploadAndGetImage({ formData, fileInput, dispatch, onChange, id });
  }

  e.target.value = '';
};

type UploadImageProps = {
  formData: FormData;
  fileInput: EventTarget & HTMLInputElement;
  dispatch: (action: Action) => void;
  onChange: (id: string, imageUrl: string, token?: string) => void;
  id: string;
};

const uploadAndGetImage = ({ formData, fileInput, dispatch, onChange, id }: UploadImageProps) => {
  fetch(routePostUploadImage(), {
    method: 'POST',
    body: formData
  })
    .then(response => response.json())
    .then((data: ApiResponse) => {
      fileInput.value = '';

      if (data.success) {
        dispatch({ type: 'image', value: data.message[0] });
        onChange(id, data.message[0]);
      } else {
        dispatch({ type: 'error', value: data.message[0] });
      }

      dispatch({ type: 'loading' });
    })
    .catch((data: ApiResponse) => {
      dispatch({ type: 'error', value: data.message[0] });
      dispatch({ type: 'loading' });
    });
};

const uploadAndGetImageZendesk = ({
  formData,
  fileInput,
  dispatch,
  onChange,
  id
}: UploadImageProps) => {
  fetch(routePostUploadFile(), {
    method: 'POST',
    body: formData
  })
    .then(response => response.json())
    .then(({ success, data, message }: ZendeskApiResponse) => {
      fileInput.value = '';

      if (success) {
        dispatch({ type: 'image', value: data.contentUrl });
        onChange(id, data.contentUrl, data.token);
      } else {
        dispatch({ type: 'error', value: message[0] });
      }

      dispatch({ type: 'loading' });
    })
    .catch((data: ApiResponse) => {
      dispatch({ type: 'error', value: data.message[0] });
      dispatch({ type: 'loading' });
    });
};
